// Thin typed fetch wrapper. All calls go through the `/api` prefix, which the dev
// server (vite.config.ts) and the production nginx (nginx.conf) both strip before
// forwarding to the NestJS API. Cookies ride along automatically — same-origin `fetch`
// sends them by default, no `credentials` option needed.
const BASE = '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The backend's error code, e.g. "invalid_credentials" (ADR-0005: API speaks codes,
     * never assembled prose). Undefined if the response body wasn't the expected shape. */
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseErrorCode(res: Response): Promise<string | undefined> {
  try {
    const body: unknown = await res.clone().json();
    if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') {
      return body.error;
    }
  } catch {
    // response wasn't JSON — fall through with no code
  }
  return undefined;
}

async function handle<T>(res: Response, path: string): Promise<T> {
  if (!res.ok) {
    const code = await parseErrorCode(res);
    throw new ApiError(`${res.status} on ${path}`, res.status, code);
  }
  return (await res.json()) as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: { accept: 'application/json' } });
  return handle<T>(res, path);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handle<T>(res, path);
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'PATCH',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handle<T>(res, path);
}

export interface HealthResponse {
  status: 'ok';
  db: 'up';
  time: string;
}
